<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" style="background-color: rgba(0,0,0,0.5)" @click.self="close">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content border-0 shadow-lg rounded-3">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold text-s">Asset Information</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div v-else class="row">
            <div class="col-12">
              <div class="decorative-card rounded-small bg-light">
                <div class="p-3">
                  <div class="row align-items-start gx-2">                                 
                    <div v-if="imageUrl" class="col-md-3 mb-3">
                      <img 
                        :src="imageUrl" 
                        class="img-fluid rounded shadow-sm"
                        :alt="asset_details.asset_name"
                        style="max-height: 250px; object-fit: cover;"
                      />
                    </div>
            
                    <div :class="imageUrl ? 'col-md-6' : 'col-md-8'">
                      <div class="d-flex align-items-start justify-content-between mb-2">
                        <div class="w-100">
                          <p class="mb-1 text-s fw-bold">
                            {{ asset_details.asset_name }}
                          </p>
                          <div v-if="asset_details.asset_description" class="mb-2">
                            <span class="text-xs fw-bold">Description:</span>
                            <p class="text-xs text-muted mb-0">{{ asset_details.asset_description }}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div v-if="asset_details.date_created" class="text-xs mb-1">
                        <span class="fw-bold">Created:</span>
                        <span class="ms-2 text-muted">{{ formatDate(asset_details.date_created) }}</span>
                      </div>

                      <div v-if="workflow_data.workflow_name" class="text-xs mb-1">
                        <span class="fw-bold">Assigned to:</span>
                        <span class="ms-2 text-muted">{{ workflow_data.workflow_name }}</span>
                      </div>

                      <div v-if="qr_code_data.link" class="text-xs mb-1">
                        <span class="fw-bold">Link:</span>
                        <a 
                          :href="qr_code_data.link" 
                          target="_blank" 
                          class="ms-2 text-primary text-decoration-none"
                          rel="noopener noreferrer"
                        >
                          <i class="fas fa-external-link-alt me-1"></i>
                          View
                        </a>
                      </div>
                    </div>
                    
                    <!-- QR Code Section -->
                    <div class="text-center" :class="imageUrl ? 'col-md-3' : 'col-md-4'">
                      <p class="text-xs fw-bold mb-3">Asset QR Code</p>
                      
                      <!-- QR Code Container -->
                      <div class="qr-code-wrapper mb-3">
                        <div v-if="qr_code_data.qr_code" class="qr-code-container bg-white p-3 rounded shadow-sm d-inline-block">
                          <img 
                            :src="qr_code_data.qr_code" 
                            class="img-fluid"
                            alt="QR Code"
                            style="width: 150px; height: 150px;"
                          />
                        </div>
                        <div v-else class="qr-code-placeholder bg-white rounded shadow-sm d-inline-flex align-items-center justify-content-center">
                          <i class="fas fa-qrcode fa-3x text-muted"></i>
                        </div>
                      </div>
                      
                      <!-- Download Button -->
                      <button 
                        v-if="qr_code_data.qr_code && qr_code_data.id"
                        @click="download_qr_code" 
                        class="btn bg-green text-white text-xxs fw-bold rounded-small shadow-sm px-3 py-2"
                      >
                        <i class="fas fa-download me-1"></i> Download QR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";
import { format_date } from '@/utils/date_formatter';
import Swal from 'sweetalert2';


export default {
  name: 'AssetInfoModal',
  props: {
    assetId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      asset_details: {},
      workflow_data: {},
      qr_code_data: {},
      imageUrl: null
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    formatDate(date) {
      return format_date(date);
    },
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetch_asset_details(this.assetId),
          this.view_asset(this.assetId)
        ]);
      } catch (error) {
        console.error("Error loading asset data", error);
      } finally {
        this.loading = false;
      }
    },
    fetch_asset_details(asset_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const GET_ASSET_DETAILS_URL = `${BASE_URL}/verify_api/workflow_management_api/get_asset_details_api/${asset_id}/`;

      return new Promise((resolve, reject) => {
        $.ajax({
          method: 'GET',
          url: GET_ASSET_DETAILS_URL,
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (response) => {
            if (response.status === 'success' && response.data) {
              this.asset_details = response.data;
              if (this.asset_details.uploaded_image_url) {
                this.imageUrl = this.asset_details.uploaded_image_url;
              } else {
                this.imageUrl = null;
              }
            }
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
      });
    },
    view_asset(asset_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const GET_ASSET_WORKFLOW_URL = `${BASE_URL}/verify_api/workflow_management_api/get_asset_workflows_api/${asset_id}/`;

      return new Promise((resolve, reject) => {
        $.ajax({
          method: 'GET',
          url: GET_ASSET_WORKFLOW_URL,
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: (response) => {
            if (response.asset_workflows && response.asset_workflows.length > 0) {
              this.qr_code_data = response.asset_workflows[0].qr_code;
              this.workflow_data = response.asset_workflows[0].workflow;
              
              const is_production = window.location.hostname !== 'localhost'
              if (this.qr_code_data.link && is_production) {
                  this.qr_code_data.link = this.qr_code_data.link.replace("http://", "https://");
              }
            }
            resolve();
          },
          error: (error) => {
            reject(error);
          },
        });
      });
    },
    download_qr_code() {
      if (!this.qr_code_data.qr_code) return;
      
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const DOWNLOAD_URL = `${BASE_URL}/verify_api/workflow_management_api/download_qr_code/${this.qr_code_data.id}/`;

      Swal.fire({
          title: 'Downloading QR Code...',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
              Swal.showLoading();
          }
      });

      const xhr = new XMLHttpRequest();
      xhr.open('GET', DOWNLOAD_URL, true);
      xhr.setRequestHeader('Authorization', `Token ${TOKEN}`);
      xhr.responseType = 'blob';

      xhr.onload = () => {
          Swal.close();
          if (xhr.status === 200) {
              const blob = new Blob([xhr.response], { type: 'image/png' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${this.asset_details.asset_name}_qr_code.png`.toLowerCase().replace(/\s+/g, '_');
              document.body.appendChild(link);
              link.click();
              setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
              }, 100);
          } else {
              Swal.fire({
                  icon: 'error',
                  text: 'Failed to download QR code.',
                  confirmButtonText: 'OK'
              });
          }
      };

      xhr.onerror = () => {
          Swal.close();
          Swal.fire({ 
              icon: 'error',
              text: 'Network error occurred while downloading QR code.',
              confirmButtonText: 'OK'
          });
      };

      xhr.send();
    }
  }
}
</script>
